"use client";

import {
  Label,
  PolarGrid,
  PolarRadiusAxis,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
} from "recharts";
import { type ChartConfig, ChartContainer } from "./ChartContainer";

export type RadialChartData = {
  name: string;
  value: number;
  fill?: string;
};

export type RadialChartProps = {
  /** Chart data */
  data: RadialChartData[];
  /** Chart configuration */
  config: ChartConfig;
  /** Center text value */
  centerValue: number | string;
  /** Center text label */
  centerLabel?: string;
  /** Start angle in degrees (default: 0) */
  startAngle?: number;
  /** End angle in degrees (default: 250) */
  endAngle?: number;
  /** Inner radius (default: 80) */
  innerRadius?: number;
  /** Outer radius (default: 110) */
  outerRadius?: number;
  /** Data key for the value (default: "value") */
  dataKey?: string;
  /** Chart container className */
  className?: string;
};

export function RadialChart({
  data,
  config,
  centerValue,
  centerLabel,
  startAngle = 0,
  endAngle = 250,
  innerRadius = 80,
  outerRadius = 110,
  dataKey = "value",
  className,
}: RadialChartProps) {
  const chartData = data.map((item, index) => ({
    ...item,
    fill:
      item.fill ||
      config[item.name]?.color ||
      `var(--chart-${((index % 5) + 1) as 1 | 2 | 3 | 4 | 5})`,
  }));

  return (
    <ChartContainer config={config} className={className}>
      <ResponsiveContainer width="100%" height="100%">
        <RadialBarChart
          data={chartData}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
        >
          <PolarGrid
            gridType="circle"
            radialLines={false}
            stroke="none"
            className="first:fill-muted last:fill-background"
            polarRadius={[86, 74]}
          />
          <RadialBar dataKey={dataKey} background cornerRadius={10} />
          <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
            <Label
              content={({ viewBox }) => {
                if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                  return (
                    <text
                      x={viewBox.cx}
                      y={viewBox.cy}
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      <tspan
                        x={viewBox.cx}
                        y={viewBox.cy}
                        className="fill-foreground font-bold text-4xl"
                      >
                        {typeof centerValue === "number"
                          ? centerValue.toLocaleString()
                          : centerValue}
                      </tspan>
                      {centerLabel && (
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy || 0) + 24}
                          className="fill-muted-foreground"
                        >
                          {centerLabel}
                        </tspan>
                      )}
                    </text>
                  );
                }
              }}
            />
          </PolarRadiusAxis>
        </RadialBarChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
}
